import { isNil } from 'lodash';
import { useCallback, useState } from 'react';
import { Button } from '@shared/components/Button';
import { Markdown } from '@shared/components/markdown/Markdown';
import { i18n } from '@shared/util/i18n';
import { AskScope } from '../api';
import { useAskSummary } from '../useAskSummary';
import { ASK_MARK_ICON, AskMark } from './AskMark';
import styles from './AiSummary.module.css';

interface Props {
  /** The Ask scope the summary is resolved against (e.g. a card). */
  scope: AskScope;
  /** Builds the prompt sent to the gateway. Called lazily on click. */
  buildPrompt: () => string;
  /**
   * A previously generated summary, if any. Held in state that outlives this
   * widget (above the view that unmounts) so the result survives navigating away
   * and back within the same card, and is lost only on refresh or leaving it.
   */
  summary: string | undefined;
  /** Stores a newly generated summary in that outer state. */
  onSummaryGenerated: (summary: string) => void;
  /** Prefix for the button's testID, e.g. "card" → "card-summarize-button". */
  testIDPrefix: string;
}

type Status = 'idle' | 'loading' | 'error';

/**
 * A "Summarize" button that turns into an inline, green summary widget. Reuses the
 * Ask streaming pipeline but presents only the final answer (no streaming/typewriter
 * effect) and offers no further interaction once shown. Shared by the card-level and
 * subform-level summary features.
 */
export function AiSummary({
  scope,
  buildPrompt,
  summary,
  onSummaryGenerated,
  testIDPrefix,
}: Props) {
  const { run } = useAskSummary();
  const [status, setStatus] = useState<Status>('idle');

  const handleSummarize = useCallback(async () => {
    setStatus('loading');
    const response = await run(buildPrompt(), scope);
    if (isNil(response)) {
      setStatus('error');
      return;
    }
    onSummaryGenerated(response);
    setStatus('idle');
  }, [run, scope, buildPrompt, onSummaryGenerated]);

  // Done: the generated summary is the source of truth and persists across
  // navigation, so it wins regardless of transient local status.
  if (!isNil(summary)) {
    return (
      <div className={styles.container}>
        <div className={styles.widget}>
          <div className={styles.header}>
            <AskMark size={20} />
            <span className={styles.headerLabel}>{i18n('aiSummary.title')}</span>
          </div>
          <div className={styles.body}>
            <Markdown tokens={[]} markdown>
              {summary}
            </Markdown>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className={styles.container}>
        <div className={styles.widget}>
          <span className={styles.thinking}>
            <AskMark animated size={20} />
            {i18n('aiSummary.thinking')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {status === 'error' && <span className={styles.error}>{i18n('aiSummary.error')}</span>}
      <Button
        variant="tertiary"
        icon={ASK_MARK_ICON}
        label={i18n('aiSummary.button')}
        onClick={handleSummarize}
        testID={`${testIDPrefix}-summarize-button`}
      />
    </div>
  );
}
